/**
 * Hooks para el módulo DDJJ (Declaraciones Juradas) — Fase 4.3
 *
 * Todas las operaciones van directo a Supabase (decisión #014).
 * useCreateDDJJ y useNominaFaltante llaman al RPC calcular_planilla_prefill;
 * useSincronizarNomina, useConfirmarDDJJ y useReabrirDDJJ llaman a sus RPC propios.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type {
  PlanillaDDJJ,
  PlanillaDetalle,
  PlanillaPrefillRow,
} from '../types/ddjj';
import type { Cupon } from '../types/cupon';
import { QK_CUPONES } from './useCupones';

/**
 * Días a partir de la emisión para calcular fecha_vencimiento del cupón.
 * Configurable en Fase 8 (Configuracion del sistema).
 */
const DIAS_VENCIMIENTO_CUPON = 30;

// ── Query Keys ────────────────────────────────────────────────────────────────

const QK = {
  all: ['planillas_ddjj'] as const,
  list: (empresaId?: string, periodoId?: string) =>
    ['planillas_ddjj', 'list', empresaId ?? 'all', periodoId ?? 'all'] as const,
  detalle: (planillaId: string) => ['planilla_detalle', planillaId] as const,
};

// ── LIST ──────────────────────────────────────────────────────────────────────

/**
 * Lista planillas_ddjj con joins a empresa y periodo.
 * Si empresaId está definido, filtra por empresa.
 * Si periodoId está definido, filtra por período.
 */
export function usePlanillas(empresaId?: string, periodoId?: string) {
  return useQuery({
    queryKey: QK.list(empresaId, periodoId),
    queryFn: async () => {
      let query = supabase
        .from('planillas_ddjj')
        .select(
          '*, empresa:empresas(id, razon_social, cuit), periodo:periodos(id, periodo, descripcion)'
        )
        .order('created_at', { ascending: false });

      if (empresaId) query = query.eq('empresa_id', empresaId);
      if (periodoId) query = query.eq('periodo_id', periodoId);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as PlanillaDDJJ[];
    },
  });
}

// ── SINGLE ────────────────────────────────────────────────────────────────────

/** Obtiene una planilla por ID, con joins a empresa y periodo. */
export function usePlanilla(planillaId: string | undefined) {
  return useQuery({
    queryKey: planillaId
      ? ['planillas_ddjj', 'single', planillaId]
      : ['planillas_ddjj', 'none'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planillas_ddjj')
        .select(
          '*, empresa:empresas(id, razon_social, cuit), periodo:periodos(id, periodo, descripcion)'
        )
        .eq('id', planillaId!)
        .single();
      if (error) throw error;
      return data as PlanillaDDJJ;
    },
    enabled: !!planillaId,
  });
}

// ── DETALLE (filas de empleados) ──────────────────────────────────────────────

export function usePlanillaDetalle(planillaId: string | undefined) {
  return useQuery({
    queryKey: planillaId ? QK.detalle(planillaId) : ['planilla_detalle', 'none'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planilla_detalle')
        .select('*, categoria:categorias(descripcion)')
        .eq('planilla_id', planillaId!)
        .order('apellido', { ascending: true });
      if (error) throw error;
      return (data ?? []) as PlanillaDetalle[];
    },
    enabled: !!planillaId,
  });
}

// ── CREATE (incluye RPC prefill + insert planilla + insert detalle) ───────────

export interface CreateDDJJInput {
  empresa_id: string;
  periodo_id: string;
}

export function useCreateDDJJ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ empresa_id, periodo_id }: CreateDDJJInput) => {
      // 1. Llamar la función SQL que devuelve los afiliados a pre-cargar (RN-24)
      const { data: prefillData, error: errPrefill } = await supabase.rpc(
        'calcular_planilla_prefill',
        { p_empresa_id: empresa_id, p_periodo_id: periodo_id }
      );
      if (errPrefill) throw errPrefill;
      const prefillRows = (prefillData ?? []) as PlanillaPrefillRow[];

      // 2. Calcular el número de secuencia para esta empresa+período
      //    (RN-22 actualizada: múltiples DDJJ por período, numeradas 1, 2, 3…)
      const { count, error: errCount } = await supabase
        .from('planillas_ddjj')
        .select('*', { count: 'exact', head: true })
        .eq('empresa_id', empresa_id)
        .eq('periodo_id', periodo_id);
      if (errCount) throw errCount;
      const numero = (count ?? 0) + 1;

      // 3. Crear el encabezado de la DDJJ (estado inicial: borrador)
      const { data: planilla, error: errPlanilla } = await supabase
        .from('planillas_ddjj')
        .insert({
          empresa_id,
          periodo_id,
          numero,
          estado: 'borrador',
          monto_total: 0,
        })
        .select('*')
        .single();
      if (errPlanilla) throw errPlanilla;

      // 4. Insertar las filas de detalle (sueldo=0, el usuario las completa en el detalle)
      if (prefillRows.length > 0) {
        const detalleRows = prefillRows.map((row) => ({
          planilla_id:          planilla.id,
          afiliado_id:          row.afiliado_id,
          relacion_laboral_id:  row.relacion_laboral_id,
          cuil:                 row.cuil,
          apellido:             row.apellido,
          nombre:               row.nombre,
          categoria_id:         row.categoria_id,
          es_afiliado:          row.es_afiliado,
          sueldo:               0,
          porcentaje_aporte:    row.porcentaje_aporte,
          aporte:               0,
        }));

        const { error: errDetalle } = await supabase
          .from('planilla_detalle')
          .insert(detalleRows);
        if (errDetalle) throw errDetalle;
      }

      return planilla as PlanillaDDJJ;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  });
}

// ── GUARDAR DETALLE (batch-update de sueldos) ─────────────────────────────────

export interface DetalleRowUpdate {
  id: string;
  sueldo: number;
  porcentaje_aporte: number;
}

/**
 * Actualiza el sueldo (y recalcula el aporte) de múltiples filas de planilla_detalle.
 * Se llama al hacer clic en "Guardar cambios" desde DDJJDetalle.
 */
export function useGuardarDetalle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      planilla_id,
      rows,
    }: {
      planilla_id: string;
      rows: DetalleRowUpdate[];
    }) => {
      for (const row of rows) {
        const aporte =
          Math.round(row.sueldo * row.porcentaje_aporte) / 100;
        const { error } = await supabase
          .from('planilla_detalle')
          .update({ sueldo: row.sueldo, aporte })
          .eq('id', row.id);
        if (error) throw error;
      }
      return planilla_id;
    },
    onSuccess: (planilla_id) => {
      qc.invalidateQueries({ queryKey: QK.detalle(planilla_id) });
    },
  });
}

// ── CONFIRMAR DDJJ ────────────────────────────────────────────────────────────

export interface ConfirmarDDJJResult {
  planilla: PlanillaDDJJ;
  cupon: Cupon;
}

/**
 * Confirma una DDJJ y genera el cupón de cobro atómicamente vía RPC SQL.
 *
 * La RPC `confirmar_ddjj` (SECURITY DEFINER):
 *   - Calcula monto_total desde planilla_detalle (autoritativo, no del cliente).
 *   - UPDATE planilla → 'confirmada' + INSERT cupón en una sola transacción.
 *   - Si algo falla, rollback automático (sin estados huérfanos).
 *   - admin_empresa solo puede confirmar planillas de su empresa.
 *
 * El parámetro `monto_total` del caller se ignora a propósito (lo calcula la
 * RPC desde el detalle); se mantiene en la firma por compatibilidad con la UI.
 */
export function useConfirmarDDJJ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: string;
      /** Ignorado — la RPC lo recalcula desde planilla_detalle. */
      monto_total?: number;
    }): Promise<ConfirmarDDJJResult> => {
      const { data, error } = await supabase.rpc('confirmar_ddjj', {
        p_planilla_id:      id,
        p_dias_vencimiento: DIAS_VENCIMIENTO_CUPON,
      });
      if (error) throw error;

      const result = data as { planilla: PlanillaDDJJ; cupon: Cupon };
      return {
        planilla: result.planilla,
        cupon:    result.cupon,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.invalidateQueries({ queryKey: QK_CUPONES.all });
    },
  });
}

// ── REABRIR DDJJ (admin_general — controlada) ─────────────────────────────────

export interface ReabrirDDJJResult {
  planilla_id: string;
  cupon_anulado_id: string | null;
  motivo: string;
  reabierto_por: string;
  reabierto_at: string;
}

/**
 * Revierte una DDJJ confirmada a borrador (RPC `reabrir_ddjj`).
 * Solo admin_general; requiere que el cupón esté pendiente y sin pagos.
 * El cupón vigente se marca 'anulado' (queda en historia).
 */
export function useReabrirDDJJ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      motivo,
    }: {
      id: string;
      motivo: string;
    }): Promise<ReabrirDDJJResult> => {
      const { data, error } = await supabase.rpc('reabrir_ddjj', {
        p_planilla_id: id,
        p_motivo:      motivo,
      });
      if (error) throw error;
      return data as ReabrirDDJJResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.invalidateQueries({ queryKey: QK_CUPONES.all });
    },
  });
}

// ── DELETE DDJJ (solo borradores — RN-26) ─────────────────────────────────────

/**
 * Elimina una DDJJ en estado Borrador.
 * El trigger SQL rechazará intentos de borrar una DDJJ Confirmada.
 */
export function useDeleteDDJJ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // .select('id') devuelve las filas borradas: si no borró ninguna (RLS la
      // oculta o ya no existe) PostgREST responde 204 sin error y la pantalla
      // parecía "no hacer nada" (ticket #699).
      const { data, error } = await supabase
        .from('planillas_ddjj')
        .delete()
        .eq('id', id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'No se pudo eliminar la DDJJ: ya no existe o no tenés permiso para eliminarla.',
        );
      }
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.invalidateQueries({ queryKey: QK_CUPONES.all });
    },
  });
}

// ── NÓMINA FALTANTE / SINCRONIZAR (ADR #044) ──────────────────────────────────

/**
 * Empleados activos de la empresa que NO están en la nómina de una DDJJ en
 * borrador (altas posteriores a la creación o a la reapertura). Se calcula con la
 * misma función SQL que arma la nómina al crear la DDJJ.
 */
export function useNominaFaltante(
  planilla:
    | Pick<PlanillaDDJJ, 'id' | 'empresa_id' | 'periodo_id' | 'estado'>
    | undefined,
  detalle: Pick<PlanillaDetalle, 'relacion_laboral_id'>[],
) {
  const enNomina = detalle
    .map((d) => d.relacion_laboral_id)
    .filter((x): x is string => !!x)
    .sort();
  return useQuery({
    queryKey: ['planilla_nomina_faltante', planilla?.id, enNomina.join(',')],
    enabled: planilla?.estado === 'borrador',
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calcular_planilla_prefill', {
        p_empresa_id: planilla!.empresa_id,
        p_periodo_id: planilla!.periodo_id,
      });
      if (error) throw error;
      const presentes = new Set(enNomina);
      return ((data ?? []) as PlanillaPrefillRow[]).filter(
        (r) => !presentes.has(r.relacion_laboral_id),
      );
    },
  });
}

export interface SincronizarNominaResult {
  planilla_id: string;
  /** Empleados agregados a la nómina (sueldo 0). */
  agregados: number;
  /** Filas cuya relación laboral ya no está activa (solo se informan, no se borran). */
  bajas: number;
  /** Total de filas de la nómina luego de sincronizar. */
  total: number;
}

/**
 * Agrega a la nómina de una DDJJ en borrador los empleados activos que falten
 * (RPC `sincronizar_nomina_ddjj`). No borra filas ni toca sueldos ya cargados.
 */
export function useSincronizarNomina() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planillaId: string): Promise<SincronizarNominaResult> => {
      const { data, error } = await supabase.rpc('sincronizar_nomina_ddjj', {
        p_planilla_id: planillaId,
      });
      if (error) throw error;
      return data as SincronizarNominaResult;
    },
    onSuccess: (_res, planillaId) => {
      qc.invalidateQueries({ queryKey: QK.detalle(planillaId) });
      qc.invalidateQueries({ queryKey: ['planilla_nomina_faltante'] });
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}

// ── PAGOS NO DEFINITIVOS DE UNA DDJJ (para avisar antes de eliminar) ──────────

/**
 * Cantidad de pagos pendientes / pre-confirmados (sin transacción de Pronto Pago)
 * ligados a la DDJJ: son los que se rechazan solos al eliminarla (ADR #044).
 */
export function usePagosNoDefinitivosDDJJ(
  planillaId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['ddjj_pagos_no_definitivos', planillaId],
    enabled: !!planillaId && enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pagos')
        .select('id, estado, transaccion_recaudacion_id')
        .eq('planilla_id', planillaId!)
        .in('estado', ['pendiente', 'pre_confirmado']);
      if (error) throw error;
      return ((data ?? []) as { transaccion_recaudacion_id: string | null }[]).filter(
        (p) => !p.transaccion_recaudacion_id,
      ).length;
    },
  });
}
